import classNames from "classnames";
import React, {
    ReactElement, ReactNode, useEffect, useRef,
} from "react";
import { TicketMessage, TicketMessageType } from "../../../../../__generated__/api";
import { TicketItem } from "../../../../../contexts/ticket-state";
import { isSameDate, tryParseDate } from "../../../../../date";
import useChatState from "../../../../chat/chat-state";
import MessageCard from "./MessageCard";
import Styles from "./MessageCard.module.scss";
import SystemMessageCard from "./SystemMessageCard";
import Time from "../../../../time/Time";

const LOADING_MESSAGES = [
    {
        id: "0",
        body: {
            html: "<p>hello</p>",
        },
        createdDate: new Date(),
        type: TicketMessageType.UserMessage,
    },
    {
        id: "1",
        body: {
            html: "<p>I have a question and I need some help.</p>",
        },
        createdDate: new Date(),
        type: TicketMessageType.UserMessage,
    },
    {
        id: "2",
        body: {
            html: "<p>Hey, Happy to help with that!</p>",
        },
        createdDate: new Date(),
        type: TicketMessageType.Triggered,
    },
    {
        id: "3",
        body: {
            html: "<p>How is your day going today?</p>",
        },
        createdDate: new Date(),
        type: TicketMessageType.AgentMessage,
    },
    {
        id: "4",
        body: {
            html: "<p>Its good. I just wanted to say I appreciate your work.</p>",
        },
        createdDate: new Date(),
        type: TicketMessageType.UserMessage,
    },

];

export interface CardProps {
    isEditable: boolean;
    ticketItem: TicketItem;
    className: string;
    messages: TicketMessage[];
    isReadOnly: boolean;
    isViewPrivate: boolean;
}

export default function Cards({
    isEditable, ticketItem, className, messages, isReadOnly, isViewPrivate,
}: CardProps): ReactElement {
    let last: TicketMessage | undefined;

    const state = useChatState();
    const { isLoading } = ticketItem.useChatDetails({ isTicketOnly: isReadOnly });

    useEffect(() => {
        const agentMessages = messages.filter(msg => msg.type === TicketMessageType.AgentMessage);
        state.setLastMessageId(agentMessages.lastElement?.id);
    }, [ticketItem.messages]);

    const scrollRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const isStickyBottom = useRef(true);

    useEffect(() => {
        if (!bottomRef.current) {
            return undefined;
        }
        const observer = new IntersectionObserver(([{ isIntersecting }]) => {
            isStickyBottom.current = isIntersecting;
        }, { threshold: 1 });
        observer.observe(bottomRef.current);
        return () => {
            observer.disconnect();
        };
    }, [bottomRef.current]);

    const onScroll = () => {
        if (!scrollRef?.current || !isStickyBottom.current) {
            return;
        }

        scrollRef.current.scrollTo({
            top: scrollRef.current.scrollHeight,
        });
    };

    useEffect(() => {
        onScroll();
    }, [ticketItem.messages, messages]);

    useEffect(() => {
        isStickyBottom.current = true;
        onScroll();

        const listeners: (() => void)[] = [];
        const images = scrollRef.current?.querySelectorAll("img");
        images?.forEach(item => {
            if (!item.complete) {
                item.addEventListener("load", onScroll);
                listeners.push(() => item.removeEventListener("load", onScroll));
            }
        });

        return () => {
            listeners.forEach(handler => handler());
        };
    }, [ticketItem, scrollRef.current]);

    let lastDate: Date;

    const shouldShowLoading = isLoading && messages?.isEmpty;
    let renderMessages = shouldShowLoading ? LOADING_MESSAGES : messages;
    if (isViewPrivate) {
        renderMessages = renderMessages.filter(message => message.type === TicketMessageType.AgentMessage
            || message.type === TicketMessageType.UserMessage
            || message.type === TicketMessageType.Triggered);
    }

    return (
        <div className={className} ref={scrollRef} data-tour-panel="chat-messages">
            <div
                className={classNames(Styles.messages, shouldShowLoading ? Styles["messages-loading"] : "")}
            >
                {renderMessages?.isEmpty && (
                    <div className="text-center mt-3">
                        No messages
                    </div>
                )}

                {renderMessages?.map(msg => {
                    const message = msg as TicketMessage;
                    const lastCopy = last;
                    last = message;

                    let messageCard: ReactNode;
                    if (message.type === TicketMessageType.AgentMessage
                        || message.type === TicketMessageType.UserMessage
                        || message.type === TicketMessageType.Triggered
                        || message.type === TicketMessageType.Comment
                        || message.type === TicketMessageType.Scheduled) {
                        messageCard = (
                            <MessageCard
                                isEditable={isEditable}
                                key={msg.id}
                                message={message}
                                previous={lastCopy}
                                ticketItem={ticketItem}
                            />
                        );
                    } else if (message.type !== TicketMessageType.Draft) {
                        messageCard = (<SystemMessageCard key={msg.id} message={message} />);
                    }

                    if (!messageCard) {
                        return undefined;
                    }

                    if (message.type === TicketMessageType.Scheduled) {
                        return messageCard;
                    }

                    const date = tryParseDate(message.createdDate);
                    if (messageCard && date && (!lastDate || !isSameDate(lastDate, date))) {
                        lastDate = date;
                        return (
                            <>
                                <div
                                    key={`${msg.id}-time`}
                                    className={Styles["date-line"]}
                                >
                                    <Time date={date} variant="date" className={Styles.time} />
                                </div>
                                {messageCard}
                            </>

                        );
                    }
                    return messageCard;
                })}

                <div ref={bottomRef} />

            </div>
        </div>
    );
}
